/**
 * SendInvoiceModal Component - Mobile-first modal for sending invoices
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Send, Close } from "@mui/icons-material";
import { useDeviceType } from "../../leads/hooks/useDeviceType.js";
import { sendInvoice } from "../services/invoiceService.js";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * SendInvoiceModal Component
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Object} props.invoice - Invoice object
 * @param {Function} props.onSuccess - Callback when invoice is sent successfully
 */
const SendInvoiceModal = ({ open, onClose, invoice, onSuccess }) => {
  const { isMobile } = useDeviceType();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customMessage, setCustomMessage] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [emailTemplate, setEmailTemplate] = useState("");

  // Generate default email template
  useEffect(() => {
    if (invoice && open) {
      const invoiceId = invoice.id || invoice._id;
      const customerName = invoice.customerName || "Valued Customer";
      const total = invoice.total || 0;
      const balance = invoice.paymentSummary?.balance || invoice.total || 0;
      const dueDate = invoice.dueDate
        ? new Date(invoice.dueDate).toLocaleDateString("en-ZA", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "N/A";

      const template = `Dear ${customerName},

Please find attached your invoice ${invoiceId} for your records.

Invoice Details:
• Invoice Number: ${invoiceId}
• Total Amount: R${total.toFixed(2)}
• Balance Due: R${balance.toFixed(2)}
• Due Date: ${dueDate}

${balance > 0 ? `Payment is due on delivery or before.` : ""}

If you have any questions or concerns, please don't hesitate to contact us.

Kind regards,
The Bubakii Team`;

      setEmailTemplate(template);
      setCustomMessage("");
      setCc("");
      setBcc("");
      setShowCc(false);
      setShowBcc(false);
      setError(null);
    }
  }, [invoice, open]);

  const handleSend = async () => {
    if (!invoice) return;

    try {
      setLoading(true);
      setError(null);

      const invoiceId = invoice.id || invoice._id;
      const result = await sendInvoice(invoiceId, {
        customMessage: customMessage || undefined,
        cc:
          showCc && cc
            ? cc
                .split(",")
                .map((e) => e.trim())
                .filter(Boolean)
            : undefined,
        bcc:
          showBcc && bcc
            ? bcc
                .split(",")
                .map((e) => e.trim())
                .filter(Boolean)
            : undefined,
      });

      if (result.success) {
        logInfo("Invoice sent successfully", { invoiceId });
        if (onSuccess) {
          onSuccess(result.data);
        }
        onClose();
      } else {
        setError(result.error || "Failed to send invoice");
        logError("Failed to send invoice", {
          invoiceId,
          error: result.error,
        });
      }
    } catch (err) {
      setError(err.message || "Failed to send invoice");
      logError("Failed to send invoice", {
        invoiceId: invoice?.id || invoice?._id,
        error: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          ...(isMobile && {
            m: 0,
            borderRadius: 0,
            height: "100%",
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography variant="h6">Send Invoice</Typography>
        {!isMobile && (
          <IconButton
            onClick={handleClose}
            disabled={loading}
            size="small"
            sx={{ ml: 2 }}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Email Template (preview)
          </Typography>
          <TextField
            multiline
            rows={12}
            value={emailTemplate}
            fullWidth
            variant="outlined"
            disabled
            sx={{
              "& .MuiInputBase-root": {
                fontFamily: "monospace",
                fontSize: "0.875rem",
                backgroundColor: "rgba(0, 0, 0, 0.02)",
              },
            }}
          />

          <TextField
            label="Custom Message (optional - will be prepended)"
            multiline
            rows={3}
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            fullWidth
            variant="outlined"
            disabled={loading}
            placeholder="Add a custom message that will appear before the invoice details..."
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showCc}
                  onChange={(e) => setShowCc(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Add CC"
            />
            {showCc && (
              <TextField
                label="CC (comma-separated emails)"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                fullWidth
                variant="outlined"
                disabled={loading}
                placeholder="email1@example.com, email2@example.com"
                size="small"
              />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={showBcc}
                  onChange={(e) => setShowBcc(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Add BCC"
            />
            {showBcc && (
              <TextField
                label="BCC (comma-separated emails)"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                fullWidth
                variant="outlined"
                disabled={loading}
                placeholder="email1@example.com, email2@example.com"
                size="small"
              />
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          flexDirection: isMobile ? "column" : "row",
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          fullWidth={isMobile}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          fullWidth={isMobile}
        >
          {loading ? "Sending..." : "Send Invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendInvoiceModal;

